import { useState, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import { BASE } from '../api.js';
import { redirectToAuthCenter } from '../App.jsx';
import { checkAndResize } from '../utils/imageResize.js';

/* 进阶报告（独立）照片上传：选择/拍摄照片 → 调 /tier2/generate-standalone → onStarted 通知父组件开始轮询
   不创建初识报告、不消耗初识每日次数 */

/* --- 环境判定：只有真 Capacitor 容器才走原生相机桥；其余环境保留拍照按钮，降级到 <input capture> --- */
function detectNativeEnv() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (ua.toLowerCase().includes('capacitor')) return true; // Capacitor 原生容器
  return false;
}
const isNativeEnv = detectNativeEnv();
// 拍照 Web 兜底：移动端 <input capture=environment> 唤起系统相机
const webCaptureAttr = { capture: 'environment' };

export default function Tier2PhotoUpload({ tier2ReportId, onStarted, title, compact = false }) {
  const { token } = useContext(AuthContext);
  const [photo, setPhoto] = useState(null); // dataURL
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setError(null);
      const dataUrl = await checkAndResize(file);
      setPhoto(dataUrl);
    } catch (err) {
      setError('照片处理失败，请换一张试试');
    }
  };

  const handlePickFromCamera = async () => {
    if (isNativeEnv) {
      try {
        setError(null);
        const { Camera, CameraSource } = await import('@capacitor/camera');
        const r = await Camera.getPhoto({ quality: 85, allowEditing: false, resultType: 1, source: CameraSource.Camera });
        setPhoto(r.dataUrl);
      } catch (err) {
        captureInputRef.current?.click();
      }
      return;
    }
    captureInputRef.current?.click();
  };

  const handlePickFromGallery = async () => {
    if (isNativeEnv) {
      try {
        setError(null);
        const { Camera, CameraSource } = await import('@capacitor/camera');
        const r = await Camera.getPhoto({ quality: 85, allowEditing: false, resultType: 1, source: CameraSource.Photos });
        setPhoto(r.dataUrl);
      } catch (err) {
        fileInputRef.current?.click();
      }
      return;
    }
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!photo || uploading) return;
    setUploading(true);
    setError(null);
    try {
      // 直接传 base64 dataURL 字符串（不用 FormData/multipart），鉴权通过共享域 cookie 自动传递
      const res = await fetch(BASE + '/tier2/generate-standalone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo, tier2ReportId: tier2ReportId || undefined }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        setError('登录已过期，请重新登录');
        redirectToAuthCenter();
        return;
      }
      if (res.status === 400) {
        setError(data?.message || data?.error || '缺少照片，请重新上传');
        return;
      }
      if (!res.ok) {
        setError(data?.message || data?.error || '生成失败，请重试');
        return;
      }
      onStarted?.(data.tier2ReportId || tier2ReportId);
    } catch {
      setError('网络异常，请重试');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={compact ? 'tier2-photo-area tier2-photo-area--compact' : 'tier2-photo-area'}>
      {title && <p className="tier2-photo-title">{title}</p>}
      {!photo ? (
        <div className="tier2-photo-actions">
          <button className="capture-action-btn capture-camera-btn" onClick={handlePickFromCamera}>
            <span className="capture-icon">📷</span> 拍照
          </button>
          <button className="capture-action-btn capture-gallery-btn" onClick={handlePickFromGallery}>
            <span className="capture-icon">🖼</span> 从相册选择
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
          <input ref={captureInputRef} type="file" accept="image/*" {...webCaptureAttr} style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
      ) : (
        <div className="tier2-photo-selected">
          <img className="tier2-photo-preview" src={photo} alt="照片预览" />
          <div className="tier2-photo-actions">
            <button className="report-unlock-btn" onClick={handleSubmit} disabled={uploading}>
              {uploading ? '提交中…' : '生成进阶报告'}
            </button>
            <button className="report-redo-btn" onClick={() => { setPhoto(null); setError(null); }}>重新选择照片</button>
          </div>
        </div>
      )}
      {error && <p className="tier2-photo-error">⚠️ {error}</p>}
    </div>
  );
}
